import { List, Search, Table2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { cn } from "@/lib/utils";
import type { SprintSort, SprintStatusFilter } from "./useSprintsFilter";

export type SprintView = "list" | "table";

const STATUS_OPTIONS = [
	{ value: "all", label: "All statuses" },
	{ value: "planned", label: "Planned" },
	{ value: "active", label: "Active" },
	{ value: "completed", label: "Completed" },
];

const SORT_OPTIONS = [
	{ value: "start_date:desc", label: "Latest first" },
	{ value: "start_date:asc", label: "Oldest first" },
	{ value: "name:asc", label: "Name A-Z" },
	{ value: "name:desc", label: "Name Z-A" },
	{ value: "status:asc", label: "Status: active first" },
	{ value: "status:desc", label: "Status: completed first" },
];

const VIEWS = [
	{ value: "list", icon: List, label: "List view" },
	{ value: "table", icon: Table2, label: "Table view" },
] as const;

export function SprintFilters({
	search,
	onSearchChange,
	status,
	onStatusChange,
	sort,
	onSortChange,
	view,
	onViewChange,
}: {
	search: string;
	onSearchChange: (v: string) => void;
	status: SprintStatusFilter;
	onStatusChange: (v: SprintStatusFilter) => void;
	sort: SprintSort;
	onSortChange: (v: SprintSort) => void;
	view: SprintView;
	onViewChange: (v: SprintView) => void;
}) {
	return (
		<div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
			<div className="relative flex-1 sm:max-w-xs">
				<Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
				<Input
					placeholder="Search sprints..."
					value={search}
					onChange={(e) => onSearchChange(e.target.value)}
					className="h-9 pl-8 text-sm"
				/>
			</div>
			<div className="flex flex-wrap items-center gap-2 sm:ml-auto sm:flex-nowrap">
				<SearchableSelect
					value={`${sort.key}:${sort.dir}`}
					onValueChange={(v) => {
						if (!v) return;
						const [key, dir] = v.split(":");
						onSortChange({ key, dir } as SprintSort);
					}}
					options={SORT_OPTIONS}
					className="min-w-0 flex-1 sm:w-52 sm:flex-none"
				/>
				<SearchableSelect
					value={status}
					onValueChange={(v) => onStatusChange((v || "all") as SprintStatusFilter)}
					options={STATUS_OPTIONS}
					className="min-w-0 flex-1 sm:w-44 sm:flex-none"
				/>
				<div className="flex shrink-0 items-center overflow-hidden rounded-lg border border-border bg-surface">
					{VIEWS.map(({ value, icon: Icon, label }) => (
						<button
							key={value}
							type="button"
							aria-label={label}
							aria-pressed={view === value}
							onClick={() => onViewChange(value)}
							className={cn(
								"p-2 transition-colors",
								view === value
									? "bg-primary text-primary-foreground"
									: "text-muted hover:text-foreground hover:bg-muted-subtle",
							)}
						>
							<Icon className="h-4 w-4" />
						</button>
					))}
				</div>
			</div>
		</div>
	);
}
